import type { OrgLike } from "./orgPlugins";
import { type PluginHookContribution, definePluginHook } from "./pluginHook";

/**
 * Input to a report data-mapping contribution.
 *
 * - `wireRow` — one raw API report row (typically snake_case wire fields). Plugins
 *   read plugin-owned values from here (e.g. `gl_code`) so core mappers never
 *   name those keys.
 * - `hostRow` — the host’s already-built camelCase row for the same record
 *   (formatting, shared columns). Passed so a contribution can derive from host
 *   fields when needed; `apply` uses it as the immutable base of the result.
 *
 * Both are required. Contributions must treat them as read-only and return a
 * new `Partial<HostRow>` fragment containing **only plugin-owned keys**.
 * Overlapping keys between plugins, or between a plugin and `hostRow`, throw.
 * `apply` does not mutate either object: when fragments are empty it returns
 * `hostRow` as-is; otherwise `{ ...hostRow, ...fragments }`.
 */
export type ReportDataMappingContext<
  HostRow extends object,
  WireRow = Record<string, unknown>,
> = {
  wireRow: WireRow;
  hostRow: HostRow;
};

export type PluginReportDataMappingHook<
  HostRow extends object,
  WireRow = Record<string, unknown>,
> = {
  readonly id: string;
  contribute(
    fn: (
      ctx: ReportDataMappingContext<HostRow, WireRow>
    ) => Partial<HostRow> | void
  ): PluginHookContribution;
  /** Merges plugin field fragments onto the host row for one wire row. */
  apply(
    organization: OrgLike,
    ctx: ReportDataMappingContext<HostRow, WireRow>
  ): HostRow;
};

function mergeHostRowFragments<Fields extends object>(
  hookName: string,
  accumulated: Partial<Fields>,
  contribution: Partial<Fields>
): Partial<Fields> {
  for (const key of Object.keys(contribution)) {
    if (Object.prototype.hasOwnProperty.call(accumulated, key)) {
      throw new Error(
        `Duplicate mapped field "${key}" contributed to hook "${hookName}".`
      );
    }
  }

  return { ...accumulated, ...contribution };
}

function assertNoHostFieldOverwrite(
  hookName: string,
  hostRow: object,
  fragments: object
): void {
  for (const key of Object.keys(fragments)) {
    if (Object.prototype.hasOwnProperty.call(hostRow, key)) {
      throw new Error(
        `Mapped field "${key}" on hook "${hookName}" overwrites a host-owned field.`
      );
    }
  }
}

/**
 * Defines a host-owned extension point for enriching mapped report rows from
 * API payloads. Plugins contribute field fragments; overlapping keys between
 * plugins or with the host `hostRow` throw.
 *
 * Legacy adapter for reports that invent camelCase rows separately from
 * headers (e.g. `maps.js`). Prefer columns-only / API consumer shape for new
 * reports. Pure relative to row data: contributions must not mutate `wireRow` /
 * `hostRow`. Hosts call `apply(organization, ctx)` per row.
 */
export function definePluginReportDataMappingHook<
  HostRow extends object,
  WireRow = Record<string, unknown>,
>(config: { name: string }): PluginReportDataMappingHook<HostRow, WireRow> {
  const fragmentsHook = definePluginHook<
    ReportDataMappingContext<HostRow, WireRow>,
    Partial<HostRow>
  >({
    name: config.name,
    initial: () => ({}),
    merge: (accumulated, contribution) =>
      mergeHostRowFragments(config.name, accumulated, contribution),
  });

  return {
    id: config.name,
    contribute(fn) {
      return fragmentsHook.contribute(fn);
    },
    apply(organization, ctx) {
      const fragments = fragmentsHook.run(organization, ctx);
      if (Object.keys(fragments).length === 0) {
        return ctx.hostRow;
      }

      assertNoHostFieldOverwrite(config.name, ctx.hostRow, fragments);

      return {
        ...ctx.hostRow,
        ...fragments,
      };
    },
  };
}
